import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { ArrowLeft, AlertCircle } from 'lucide-react'
import { loadPlay } from '../services/playsApi'
import NavigationHeader from '../components/NavigationHeader'
import { useNavigate } from 'react-router-dom'

const PlayViewPage = () => {
  const { playId } = useParams()
  const navigate = useNavigate()
  const [play, setPlay] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadPlayData = async () => {
      if (!playId) {
        setError('No play ID provided')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const loadedPlay = await loadPlay(playId)
        setPlay(loadedPlay)
      } catch (err) {
        console.error('Error loading play:', err)
        setError(err.message || 'Failed to load play')
      } finally {
        setIsLoading(false)
      }
    }

    loadPlayData()
  }, [playId])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading play...</p>
        </div>
      </div>
    )
  }

  if (error || !play) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg border border-red-200 p-6 text-center">
            <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Play Not Found</h2>
            <p className="text-gray-600 mb-4">
              {error || 'The play you are looking for does not exist or you do not have access to it.'}
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!play.gifUrl) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg border border-yellow-200 p-6 text-center">
            <AlertCircle size={48} className="text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No GIF Available</h2>
            <p className="text-gray-600 mb-4">
              This play does not have a GIF yet.
            </p>
            <button
              onClick={() => navigate('/home')}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <style>{`
        @media (orientation: landscape) and (max-width: 768px) {
          .play-view-container {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
          }
          .play-view-back-button {
            display: none;
          }
          .play-view-header {
            padding: 0.5rem;
            margin-bottom: 0.5rem;
          }
          .play-view-header h1 {
            font-size: 1rem;
            margin-bottom: 0.25rem;
          }
          .play-view-header p {
            font-size: 0.75rem;
            margin-bottom: 0.25rem;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .play-view-sport-type {
            display: none;
          }
          .play-view-gif-container {
            padding: 0.25rem;
          }
          .play-view-gif {
            max-height: 90vh !important;
          }
        }
      `}</style>
      <div className="min-h-screen bg-gray-50">
        <NavigationHeader />
        
        <div className="max-w-4xl mx-auto px-2 sm:px-4 py-2 sm:py-4 md:py-6 play-view-container">
          {/* Back Button - Hidden in landscape on mobile */}
          <button
            onClick={() => navigate('/home')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-2 sm:mb-4 md:mb-6 play-view-back-button transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="text-sm md:text-base">Back to Home</span>
          </button>

          {/* Play Header - Compact in landscape */}
          <div className="bg-white rounded-lg border border-gray-200 p-2 sm:p-4 md:p-6 mb-2 sm:mb-4 md:mb-6 play-view-header">
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
              {play.name || 'Untitled Play'}
            </h1>
            
            {play.description && (
              <p className="text-xs sm:text-sm md:text-base text-gray-600 mb-2 sm:mb-4">
                {play.description}
              </p>
            )}

            {play.sportType && (
              <div className="play-view-sport-type">
                <span className="inline-block px-2 py-0.5 sm:px-3 sm:py-1 text-xs md:text-sm bg-gray-100 text-gray-700 rounded-full capitalize">
                  {play.sportType}
                </span>
              </div>
            )}
          </div>

          {/* GIF Display - Optimized for landscape */}
          <div className="bg-white rounded-lg border border-gray-200 p-1 sm:p-2 md:p-4 lg:p-6 play-view-gif-container">
            <div className="flex flex-col items-center">
              <img
                src={play.gifUrl}
                alt={play.name || 'Play animation'}
                className="max-w-full h-auto rounded-lg shadow-lg w-full play-view-gif"
                style={{ 
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none'
                  const errorDiv = document.createElement('div')
                  errorDiv.className = 'text-center p-4 sm:p-8 text-gray-500'
                  errorDiv.innerHTML = `
                    <AlertCircle size={48} class="mx-auto mb-4 text-gray-400" />
                    <p>Failed to load GIF. The image may have been deleted or is no longer available.</p>
                  `
                  e.target.parentNode.appendChild(errorDiv)
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PlayViewPage

